<template>
  <div class="date-field-control">
    <button
      type="button"
      class="date-field-input"
      :class="{ open }"
      @click.stop="toggle"
    >
      <span v-if="modelValue">{{ display }}</span>
      <span v-else class="date-field-placeholder">{{ placeholder || '—' }}</span>
    </button>

    <!-- In the body, so the card this sits in cannot clip it. -->
    <Teleport to="body">
      <div v-if="open" class="date-menu" :style="menuStyle">
        <div class="date-menu-head">
          <button type="button" class="date-menu-step" @click="shiftMonth(-1)" :aria-label="$t('common.previous')">
            <LineIcon name="chevron-left" :size="14" :stroke-width="1.8" />
          </button>
          <span class="date-menu-month">{{ monthLabel }}</span>
          <button type="button" class="date-menu-step" @click="shiftMonth(1)" :aria-label="$t('common.next')">
            <LineIcon name="chevron-right" :size="14" :stroke-width="1.8" />
          </button>
        </div>

        <div class="date-menu-weekdays">
          <span v-for="day in weekdays" :key="day">{{ day }}</span>
        </div>

        <div class="date-menu-grid">
          <button
            v-for="cell in cells"
            :key="cell.iso"
            type="button"
            class="date-menu-day"
            :class="{
              'is-outside': !cell.inMonth,
              'is-selected': cell.iso === modelValue,
              'is-today': cell.iso === todayIso,
            }"
            :disabled="cell.disabled"
            @click="pick(cell)"
          >
            {{ cell.day }}
          </button>
        </div>

        <div class="date-menu-foot">
          <button type="button" class="date-menu-action" @click="clear">
            {{ $t('common.clear') }}
          </button>
          <button type="button" class="date-menu-action" @click="pickToday">
            {{ $t('common.today') }}
          </button>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script setup>
/**
 * A date field with the app's own calendar under it.
 *
 * `<input type="date">` is styled by the browser, not by us: the popup it
 * opens is drawn by the browser itself and no stylesheet reaches it — which
 * is why it arrived in a different design, in English, with mm/dd/yyyy.
 *
 * So the field is a button showing the date, and the calendar is one more of
 * the app's dropdowns: the same white panel, hairline border, soft shadow and
 * blue tint, put in the body so nothing clips it and placed under the button
 * that opened it.
 *
 * The value stays what an <input type="date"> would hold — 'YYYY-MM-DD' — so
 * it drops into anything that was bound to one.
 */
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import LineIcon from '@/components/icons/LineIcon.vue';

const props = defineProps({
    modelValue: { type: String, default: '' },
    min: { type: String, default: '' },
    max: { type: String, default: '' },
    placeholder: { type: String, default: '' },
});

const emit = defineEmits(['update:modelValue', 'change']);

const { locale } = useI18n();

const open = ref(false);
const menuStyle = ref({});

/** The month on show, as the first of it. */
const cursor = ref(startOfMonth(props.modelValue || today()));

function today() {
    return toIso(new Date());
}

function toIso(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
}

function parse(iso) {
    const [year, month, day] = (iso || '').split('-').map(Number);

    return year ? new Date(year, month - 1, day) : new Date();
}

function startOfMonth(iso) {
    const date = parse(iso);

    return new Date(date.getFullYear(), date.getMonth(), 1);
}

const todayIso = computed(() => today());

const display = computed(() => {
    if (!props.modelValue) {
        return '';
    }

    return parse(props.modelValue).toLocaleDateString(locale.value, {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
    });
});

const monthLabel = computed(() => cursor.value.toLocaleDateString(locale.value, {
    month: 'long',
    year: 'numeric',
}));

/* Monday first: this is a Bulgarian product, and the week starts there. */
const weekdays = computed(() => {
    const names = [];

    for (let i = 0; i < 7; i++) {
        // 2024-01-01 was a Monday, so this walks Mon..Sun whatever the locale.
        const day = new Date(2024, 0, 1 + i);
        names.push(day.toLocaleDateString(locale.value, { weekday: 'short' }).slice(0, 2));
    }

    return names;
});

/* Six weeks, always: a grid that changes height as months change is a grid
   that moves the day you were about to press. */
const cells = computed(() => {
    const first = cursor.value;
    const offset = (first.getDay() + 6) % 7; // Monday = 0
    const start = new Date(first.getFullYear(), first.getMonth(), 1 - offset);
    const out = [];

    for (let i = 0; i < 42; i++) {
        const date = new Date(start.getFullYear(), start.getMonth(), start.getDate() + i);
        const iso = toIso(date);

        out.push({
            iso,
            day: date.getDate(),
            inMonth: date.getMonth() === first.getMonth(),
            /* Boolean, not the expression's own value: with no min and no max
               it evaluates to '' — and `disabled=""` is a disabled button, so
               every day in the month came out greyed and unclickable. */
            disabled: Boolean(
                (props.min && iso < props.min) || (props.max && iso > props.max),
            ),
        });
    }

    return out;
});

const shiftMonth = (by) => {
    cursor.value = new Date(cursor.value.getFullYear(), cursor.value.getMonth() + by, 1);
};

const place = (button) => {
    const box = button.getBoundingClientRect();

    menuStyle.value = {
        top: `${box.bottom + 4}px`,
        // Kept inside the viewport: the field is often near the right edge.
        left: `${Math.max(8, Math.min(box.left, window.innerWidth - 268))}px`,
    };
};

const toggle = (event) => {
    if (open.value) {
        open.value = false;

        return;
    }

    cursor.value = startOfMonth(props.modelValue || today());
    place(event.currentTarget);
    open.value = true;
};

const close = () => {
    open.value = false;
};

const pick = (cell) => {
    if (cell.disabled) {
        return;
    }

    close();
    emit('update:modelValue', cell.iso);
    emit('change', cell.iso);
};

const pickToday = () => {
    const iso = today();

    pick({
        iso,
        disabled: Boolean(
            (props.min && iso < props.min) || (props.max && iso > props.max),
        ),
    });
};

const clear = () => {
    close();
    emit('update:modelValue', '');
    emit('change', '');
};

const onOutside = (event) => {
    if (!open.value) return;
    if (event.target.closest('.date-menu') || event.target.closest('.date-field-input')) return;
    close();
};

const onKey = (event) => {
    if (event.key === 'Escape') close();
};

watch(() => props.modelValue, (value) => {
    if (value) cursor.value = startOfMonth(value);
});

/* Capture, to hear a scrolling container move the field out from under the
   calendar — but not the calendar's own. */
const onScroll = (event) => {
    if (event?.target?.closest?.('.date-menu')) return;
    close();
};

onMounted(() => {
    document.addEventListener('pointerdown', onOutside, true);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', close);
});

onUnmounted(() => {
    document.removeEventListener('pointerdown', onOutside, true);
    document.removeEventListener('keydown', onKey);
    window.removeEventListener('scroll', onScroll, true);
    window.removeEventListener('resize', close);
});
</script>

<style scoped>
.date-field-control {
    display: inline-flex;
}

/* A class the call site kept — form-input, form-control, filter-input — lands
   on this root, because that is where Vue puts a fall-through class, and it
   brings a native field's padding, border and background with it. The frame
   is the button's job, so the root carries none of it. !important because the
   page's rule and this one are of equal weight and the page's comes later. */
.date-field-control {
    padding: 0 !important;
    border: none !important;
    background: none !important;
    box-shadow: none !important;
    height: auto !important;
    min-height: 0 !important;
    line-height: normal !important;
}

/* And the button fills whatever width the field was given. */
.date-field-control {
    width: 100%;
}

.date-field-input {
    width: 100%;
}

/* The field itself: the same quiet control as the pickers beside it. */
.date-field-input {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    min-width: 108px;
    padding: 6px 12px;
    background: #fff;
    border: 1px solid var(--border, #e2e8f0);
    border-radius: 8px;
    font-family: inherit;
    font-size: 13px;
    color: var(--text-primary);
    text-align: left;
    cursor: pointer;
    transition: all 0.2s ease;
    /* Room for the chevron drawn on the right. */
    padding-right: 26px;
    appearance: none;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 12 12' fill='none' stroke='%23374151' stroke-width='1.6' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m2.5 4.5 3.5 3.5 3.5-3.5'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 9px center;
}

.date-field-input:hover {
    border-color: var(--primary);
}

.date-field-input.open {
    border-color: var(--primary);
    color: var(--primary);
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 12 12' fill='none' stroke='%232b82f0' stroke-width='1.6' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m2.5 7.5 3.5-3.5 3.5 3.5'/%3E%3C/svg%3E");
}

.date-field-input:focus-visible {
    outline: 2px solid var(--primary);
    outline-offset: 1px;
}

.date-field-placeholder {
    color: var(--text-light, #64748b);
}

/* The calendar, dressed like every other menu in the app. */
.date-menu {
    position: fixed;
    /* Above every overlay in the app — at 1000 it opened behind any modal. */
    z-index: 100100;
    width: 252px;
    padding: 8px;
    background: #fff;
    border: 1px solid var(--border, #e2e8f0);
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    font-family: inherit;
}

.date-menu-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 6px;
    padding: 2px 2px 6px;
}

.date-menu-month {
    font-size: 12px;
    font-weight: 700;
    color: var(--text-primary);
    text-transform: capitalize;
}

.date-menu-step {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    padding: 0;
    border: none;
    border-radius: 6px;
    background: transparent;
    color: var(--primary);
    cursor: pointer;
    transition: background 0.15s ease;
}

.date-menu-step:hover {
    background: var(--primary-light, #e0f2fe);
}

.date-menu-weekdays,
.date-menu-grid {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    gap: 1px;
}

.date-menu-weekdays span {
    padding: 2px 0;
    font-size: 10px;
    font-weight: 600;
    color: var(--text-light, #64748b);
    text-align: center;
    text-transform: uppercase;
}

.date-menu-day {
    height: 28px;
    padding: 0;
    border: none;
    border-radius: 6px;
    background: transparent;
    font-family: inherit;
    font-size: 12px;
    font-weight: 500;
    color: var(--text-primary);
    cursor: pointer;
    transition: background 0.15s ease;
}

.date-menu-day:hover:not(:disabled) {
    background: var(--primary-light, #e0f2fe);
}

/* Days either side of the month stay in place but step back. */
.date-menu-day.is-outside {
    color: var(--text-light, #cbd5e1);
}

.date-menu-day:disabled {
    color: #dbe2ea;
    cursor: default;
}

/* Today is marked by a ring, the chosen day by a fill — so the two can be
   the same day without one hiding the other. */
.date-menu-day.is-today {
    box-shadow: inset 0 0 0 1px var(--primary);
}

.date-menu-day.is-selected {
    background: var(--primary);
    color: #fff;
    font-weight: 700;
}

.date-menu-foot {
    display: flex;
    justify-content: space-between;
    margin-top: 6px;
    padding-top: 6px;
    border-top: 1px solid var(--gray-lighter, #f1f5f9);
}

.date-menu-action {
    padding: 3px 6px;
    border: none;
    border-radius: 6px;
    background: transparent;
    font-family: inherit;
    font-size: 11px;
    font-weight: 600;
    color: var(--primary);
    cursor: pointer;
}

.date-menu-action:hover {
    background: var(--primary-light, #e0f2fe);
}
</style>
